import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  SearchBox,
  StatePill,
  apiErrorMessage,
} from "@/components/admin/AdminUI";

const EMPTY = { name: "", code: "", cnpj: "", contact: "", phone: "", email: "" };

function CreateCompanyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Informe o nome da empresa.");
    setSaving(true);
    try {
      const { data } = await api.post("/clients", { ...form, name: form.name.trim() });
      toast.success("Empresa cadastrada. Defina agora os módulos contratados.");
      onOpenChange(false);
      onCreated(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="company-create-dialog">
        <DialogHeader>
          <DialogTitle>Nova empresa cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="company-name">Razão social / nome *</Label>
            <Input id="company-name" value={form.name} onChange={set("name")} autoFocus data-testid="company-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="company-code">Sigla (protocolos)</Label>
              <Input id="company-code" value={form.code} onChange={set("code")} maxLength={6} data-testid="company-code-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-cnpj">CNPJ</Label>
              <Input id="company-cnpj" value={form.cnpj} onChange={set("cnpj")} data-testid="company-cnpj-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="company-contact">Contato</Label>
              <Input id="company-contact" value={form.contact} onChange={set("contact")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-phone">Telefone</Label>
              <Input id="company-phone" value={form.phone} onChange={set("phone")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="company-email">E-mail</Label>
            <Input id="company-email" type="email" value={form.email} onChange={set("email")} />
          </div>
          <p className="text-xs text-muted-foreground">
            A empresa nasce sem módulos contratados (nenhum acesso é concedido por padrão).
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="company-create-submit">Cadastrar empresa</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCompanies() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api
      .get("/admin-center/tenants", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => {
        setItems([]);
        toast.error(apiErrorMessage(err));
      });
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-companies">
      <PageHeader
        icon={Building}
        title="Empresas clientes"
        description="Cada empresa é um tenant isolado: contrato de módulos, unidades, usuários e status de acesso."
        actions={
          <Button onClick={() => setCreateOpen(true)} data-testid="company-create-btn">
            <Plus size={15} className="mr-1" /> Nova empresa
          </Button>
        }
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <SearchBox value={search} onChange={setSearch} placeholder="Nome, sigla ou CNPJ" testId="company-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-44" aria-label="Filtrar por status" data-testid="company-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="suspended">Suspensas</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {items === null ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState title="Nenhuma empresa encontrada" testId="companies-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="companies-table">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Unidades</th>
                  <th className="px-4 py-3 font-medium text-right">Usuários</th>
                  <th className="hidden px-4 py-3 font-medium md:table-cell">Módulos contratados</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-accent/40"
                    onClick={() => navigate(`/admin/empresas/${item.id}`)}
                    data-testid={`company-row-${item.id}`}
                  >
                    <td className="px-4 py-3">
                      <Link to={`/admin/empresas/${item.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                        {item.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">{[item.code, item.cnpj].filter(Boolean).join(" · ") || "—"}</p>
                    </td>
                    <td className="px-4 py-3">
                      {item.active ? <StatePill tone="success">Ativa</StatePill> : <StatePill tone="danger">Suspensa</StatePill>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{item.units}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{item.users}</td>
                    <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
                      {item.contracted_entries.length ? `${item.contracted_entries.length} módulo(s)` : "Nenhum"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CreateCompanyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(client) => navigate(`/admin/empresas/${client.id}?tab=contrato`)}
      />
    </div>
  );
}
